import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, ChevronLeft, Camera, MoreVertical, X, Image as Galeria, CheckCheck, ClipboardPaste, Square, CheckSquare, Trash2 } from 'lucide-react';
import { decidirApagarLote, JANELA_APAGAR_PARA_TODOS_MIN } from '@fiodecobre/shared';
import { appApi, ErroApp, type ClienteApp, type MensagemApp } from './api';
import { GravadorVoz } from './GravadorVoz';
import { PlayerAudio } from './PlayerAudio';
import { BotaoCompartilhar } from './BotaoCompartilhar';
import { AnexoDocumento } from './AnexoDocumento';
import { limparBadge } from './push';
import { fotoDoColar, fotoDoClipboard, ErroClipboard } from './colar-foto';

// O chat do cliente com a loja.
//
// ⚠️ **Le por consulta repetida, nao por Socket.IO.** O barramento de tempo real do
// sistema emite `io.emit` GLOBAL — quem conecta recebe as mensagens de todas as
// conversas das duas caixas. Dar credencial de socket ao cliente antes de existir
// sala dedicada abriria a base de conversas da loja inteira. Consultar a cada
// poucos segundos custa quase nada num chat de loja; a troca por socket vem junto
// da sala, que e' o mesmo trabalho que a chamada de voz vai exigir.
//
// A consulta e' INCREMENTAL (`?after=<ultimo id>`) — carrega o historico uma vez e
// depois so pergunta o que chegou de novo.

interface Props {
  cliente: ClienteApp;
  /** So pro caso de sessao vencida no meio da consulta — ver o `catch` do 401. O
   *  botao de sair mora na HOME desde a [DA-575], nao mais neste cabecalho. */
  aoSair: () => void;
  /** Volta pra home. O chat deixou de ser a tela raiz do app em 18/08/2026. */
  aoVoltar: () => void;
}

const COBRE = '#b45309';
const INTERVALO_ATIVO = 4000;
const INTERVALO_OCIOSO = 20000;

// As seis do WhatsApp. Conjunto FECHADO de proposito: o ERP usa o teclado de emoji
// inteiro, que aqui seria um painel enorme num celular pra escolher, na pratica,
// sempre o joia.
const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// ⚠️ A consulta incremental (`?after=<id>`) so enxerga mensagem NOVA — ela e' cega
// pra mudanca em mensagem que ja esta na tela, e reacao e' exatamente isso. Sem
// esta releitura periodica, a loja reagiria e o cliente nunca veria (ele veria so
// as proprias reacoes, que a resposta do POST devolve). A cada N ciclos a tela
// relê o rodape da conversa inteiro e MESCLA por id.
const CICLOS_ATE_RELER = 5;
const RELEITURA_LIMITE = 40;

// Foto de celular chega com 3000px+ e alguns MB. A loja precisa ENXERGAR a peca,
// nao arquivar um master — 1600px no lado maior le etiqueta e codigo sem problema
// e derruba o envio pra algumas centenas de KB (o 4G da obra agradece).
// ⚠️ O original NAO se perde: ele continua na galeria do celular do cliente. Se um
// dia esta foto virar documento (laudo, comprovante), esta conta muda.
const FOTO_LADO_MAX = 1600;
const FOTO_QUALIDADE = 0.85;

async function reduzirFoto(arquivo: File): Promise<{ base64: string; mime: string }> {
  const bitmap = await createImageBitmap(arquivo);
  const escala = Math.min(1, FOTO_LADO_MAX / Math.max(bitmap.width, bitmap.height));
  const largura = Math.round(bitmap.width * escala);
  const altura = Math.round(bitmap.height * escala);
  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nao consegui preparar a foto.');
  ctx.drawImage(bitmap, 0, 0, largura, altura);
  bitmap.close?.();
  const dataUrl = canvas.toDataURL('image/jpeg', FOTO_QUALIDADE);
  return { base64: dataUrl.split(',')[1] ?? '', mime: 'image/jpeg' };
}

function horaCurta(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function diaLegivel(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  const mesmoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (mesmoDia(d, hoje)) return 'Hoje';
  if (mesmoDia(d, ontem)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
}

/**
 * O anexo compartilhavel da mensagem, quando ha um.
 *
 * Texto nao entra de proposito: o celular ja copia texto sozinho ([DA-524] p.7).
 */
function anexoDe(m: MensagemApp): string | null {
  return m.midia_url && (m.tipo === 'image' || m.tipo === 'audio' || m.tipo === 'document') ? m.midia_url : null;
}

function rotuloCompartilhar(m: MensagemApp): string {
  return m.tipo === 'audio' ? 'Compartilhar este áudio'
    : m.tipo === 'document' ? 'Compartilhar este arquivo'
      : 'Compartilhar esta foto';
}

/** A reacao do CLIENTE — a `fromMe` do formato compartilhado e' a da LOJA. */
function reacaoDoCliente(m: MensagemApp): string | null {
  return (m.reactions ?? []).find((r) => !r.fromMe)?.emoji ?? null;
}

/**
 * Cabecalho da folha de opcoes: de QUEM e' a mensagem e de quando.
 *
 * Existe porque a folha sobe por cima do balao — sem a linha, a pessoa fica com uma
 * lista de acoes e nenhuma pista de sobre o que elas agem, e uma delas leva a apagar.
 *
 * ⚠️ O genero muda a frase ("Sua foto" x "Seu audio"), entao os dois lados de cada
 * par vem escritos JUNTOS em vez de montados por concatenacao.
 */
function tituloDaMensagem(m: MensagemApp): string {
  const par: [string, string] =
    m.tipo === 'image' ? ['Sua foto', 'Foto da loja']
      : m.tipo === 'audio' ? ['Seu áudio', 'Áudio da loja']
        : m.tipo === 'document' ? ['Seu arquivo', 'Arquivo da loja']
          : ['Sua mensagem', 'Mensagem da loja'];
  const quem = m.direcao === 'inbound' ? par[0] : par[1];
  return `${quem} · ${diaLegivel(m.criado_em).toLowerCase()} ${horaCurta(m.criado_em)}`;
}

export function ChatApp({ cliente, aoSair, aoVoltar }: Props) {
  const [mensagens, setMensagens] = useState<MensagemApp[]>([]);
  const [texto, setTexto] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /**
   * Mensagem cuja folha de opcoes esta aberta (uma por vez) — o "..." de cada balao.
   *
   * Substituiu, em 18/08/2026, a fileira de emojis solta e o par de botoes ao lado do
   * balao ([DA-573]).
   */
  const [menuMsg, setMenuMsg] = useState<number | null>(null);
  /**
   * Apagar em lote (18/08/2026, pedido dele).
   *
   * ⚠️ **So as MINHAS mensagens entram na selecao** — espelho da regra que a loja ja
   * seguia ([DA-541]): ninguem apaga a fala do outro do aparelho do outro. O servidor
   * cobra isso de novo; aqui e' so o que a tela deixa marcar.
   *
   * ⚠️ **Esta lista E' o modo de selecao** — nao ha booleano separado ([DA-573]). Com
   * ela vazia nao ha modo; com qualquer item, as caixinhas aparecem. Um `modoSelecao`
   * proprio permitiria o estado "no modo, sem nada marcado", que e' beco sem saida:
   * a barra que oferece o Cancelar so existe quando ha selecao.
   */
  const [selecionadas, setSelecionadas] = useState<number[]>([]);
  const [apagando, setApagando] = useState(false);
  const [confirmandoApagar, setConfirmandoApagar] = useState(false);
  // Guarda a data junto da URL: ela vira o nome do arquivo no compartilhamento, e
  // aqui nao ha o registro da mensagem em maos.
  const [fotoAberta, setFotoAberta] = useState<{ url: string; criadoEm: string } | null>(null);
  // Folha de escolha da foto (tirar agora x pegar da galeria). Ver o bloco do
  // botao da camera, no composer, pro motivo dela existir.
  const [menuFoto, setMenuFoto] = useState(false);
  const fotoInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const rolagemRef = useRef<HTMLDivElement | null>(null);
  // Enquanto verdadeiro, a tela ACOMPANHA o rodape: tudo que fizer a conversa
  // crescer depois de desenhada puxa a rolagem de volta pra ultima mensagem.
  // Vira falso assim que a pessoa sobe pra ler — quem esta lendo no meio nao
  // pode ser jogado pro fim por uma foto que acabou de chegar.
  const seguirFimRef = useRef(true);
  // Ultimo topo que NOS escrevemos — e' o que separa a rolagem da pessoa da nossa.
  const topoPostoRef = useRef(-1);
  const ultimoIdRef = useRef<number>(0);
  // `aoSair` vem do componente pai e muda de identidade a cada render dele; sem o
  // ref, o efeito de consulta se remontaria sozinho e reiniciaria o relogio.
  const aoSairRef = useRef(aoSair);
  aoSairRef.current = aoSair;

  const descer = useCallback((suave: boolean) => {
    const el = rolagemRef.current;
    if (!el) return;
    seguirFimRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: suave ? 'smooth' : 'auto' });
    // A rolagem instantanea ja valeu nesta linha; guardar onde ela parou e' o que
    // permite reconhecer, la embaixo, o eco dela chegando atrasado.
    if (!suave) topoPostoRef.current = el.scrollTop;
  }, []);

  // ⚠️ **A foto so ocupa espaco DEPOIS de carregar, e ate la o fim da conversa e'
  // mentira.** O `<img>` sobe sem altura reservada (largura fixa, altura pela
  // proporcao do arquivo), entao no instante em que os baloes sao desenhados a
  // conversa inteira "cabe na tela" — descer ali chega num rodape que ainda nao
  // existe. Medido em 17/08/2026 na conversa da CRISTINA, que e' 19 fotos em 21
  // mensagens (orcamentos, que sao JPGs compridos): a rolagem desceu com a
  // conversa em 1.026px e as fotos levaram a altura pra 5.004px logo depois, com
  // o topo parado em 506 — ou seja, a 11% do caminho, que le como "abriu no
  // comeco". Redescer a cada foto que termina e' o que fecha o buraco; a
  // alternativa (reservar a altura antes) exigiria saber a dimensao do arquivo,
  // que o servidor nao guarda.
  const aoCarregarFoto = useCallback(() => {
    if (seguirFimRef.current) descer(false);
  }, [descer]);

  // ⚠️ **O evento de rolagem chega DEPOIS, e a conversa pode ter crescido no meio
  // do caminho — entao "esta longe do fim" NAO prova que alguem subiu a tela.**
  // Medido em 17/08/2026: duas fotos terminaram entre a nossa descida e o evento
  // dela, e a conta deu 608px de distancia sem ninguem ter tocado no aparelho; o
  // acompanhamento desligava sozinho faltando as duas ultimas fotos, e a tela
  // parava a 58% da conversa. Comparar com o topo que NOS escrevemos separa os
  // dois casos: se o valor ainda e' o nosso, o evento e' eco, nao gente.
  const aoRolar = useCallback(() => {
    const el = rolagemRef.current;
    if (!el) return;
    if (el.scrollTop === topoPostoRef.current) return;
    seguirFimRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Junta o que chegou: acrescenta as ineditas e ATUALIZA as que ja estavam na tela.
  // A atualizacao existe por causa da reacao — o conteudo de uma mensagem nao muda,
  // mas o emoji que a loja pos nela muda depois de ela ja estar desenhada.
  const juntar = useCallback((novas: MensagemApp[]) => {
    if (!novas.length) return;
    setMensagens((atuais) => {
      const porId = new Map(atuais.map((m) => [m.id, m] as const));
      let mudou = false;
      for (const nova of novas) {
        // ⚠️ **Apagada para todos: a linha SOME daqui, sem deixar marca.** O pedido do
        // Ruan foi "sem rastro nas duas pontas"; a versao anterior deixava um balao
        // "Mensagem apagada" e ele cortou. O registro continua chegando do servidor
        // (vazio, so com a marca) porque e' o UNICO jeito de esta tela descobrir que
        // aquela mensagem morreu — a consulta e' incremental por id e nunca enxerga
        // uma linha deixar de existir. Ela chega pra ser removida, nao pra ser
        // desenhada.
        if (nova.apagada) {
          if (porId.delete(nova.id)) mudou = true;
          continue;
        }
        const antiga = porId.get(nova.id);
        if (!antiga) { porId.set(nova.id, nova); mudou = true; continue; }
        // ⚠️ `lida_loja` entra aqui pelo mesmo motivo da reacao, e o esquecimento seria
        // invisivel: o check ficaria cinza pra sempre numa tela que RECEBE o valor certo
        // a cada releitura e o descarta no merge. Campo mutavel novo nesta entidade
        // obriga a mexer nesta linha (DA-522 p.6).
        if (
          JSON.stringify(antiga.reactions ?? []) !== JSON.stringify(nova.reactions ?? []) ||
          antiga.lida_loja !== nova.lida_loja
        ) {
          porId.set(nova.id, { ...antiga, reactions: nova.reactions, lida_loja: nova.lida_loja });
          mudou = true;
        }
      }
      if (!mudou) return atuais;
      return [...porId.values()].sort((a, b) => a.id - b.id);
    });
    ultimoIdRef.current = Math.max(ultimoIdRef.current, ...novas.map((m) => m.id));
  }, []);

  // Troca a reacao SO na tela (a resposta do servidor ja veio confirmada).
  const aplicarReacoes = useCallback((mensagemId: number, reactions: MensagemApp['reactions']) => {
    setMensagens((atuais) => atuais.map((m) => (m.id === mensagemId ? { ...m, reactions } : m)));
  }, []);

  // Carga inicial + consulta incremental.
  useEffect(() => {
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let ciclo = 0;

    async function buscar(primeira: boolean) {
      ciclo += 1;
      const releitura = !primeira && ciclo % CICLOS_ATE_RELER === 0;
      // A tela esta na frente da pessoa AGORA? Isto vai junto da consulta e decide
      // duas coisas: o servidor marcar a leitura, e o numero do icone ser apagado.
      // Lido a cada volta (nao uma vez so) porque o app passa o dia alternando
      // entre bolso e mao.
      const visivel = !document.hidden;
      try {
        const anterior = ultimoIdRef.current;
        const r = await appApi.mensagens(
          primeira || releitura ? null : anterior || null,
          releitura ? RELEITURA_LIMITE : undefined,
          visivel,
        );
        if (!vivo) return;
        juntar(r.mensagens);
        // Ele esta olhando a conversa: o numero do icone perdeu a razao de existir.
        if (visivel) limparBadge();
        // Rolar so quando entrou mensagem NOVA — a releitura traz as mesmas de
        // sempre, e descer a tela a cada 20s tiraria o cliente de onde ele estava
        // lendo.
        const chegouNova = r.mensagens.some((m) => m.id > anterior);
        if (primeira) {
          setCarregando(false);
          requestAnimationFrame(() => descer(false));
        } else if (chegouNova) {
          requestAnimationFrame(() => descer(true));
        }
      } catch (err) {
        if (!vivo) return;
        // Sessao vencida ou revogada: volta pra tela de entrada em vez de ficar
        // tentando pra sempre contra um token que nao vale mais.
        if ((err as ErroApp).status === 401) { aoSairRef.current(); return; }
        setCarregando(false);
      }
      if (!vivo) return;
      // Aba escondida no bolso nao precisa do mesmo ritmo — respeitar isso e' o
      // que faz a consulta repetida nao virar bateria queimada.
      timer = setTimeout(() => buscar(false), document.hidden ? INTERVALO_OCIOSO : INTERVALO_ATIVO);
    }

    // Voltou pro primeiro plano: consulta JA, sem esperar o timer.
    //
    // ⚠️ Sem isto, quem toca a notificacao e cai no app veria o numero na bolinha
    // sobreviver por ate 20 segundos — que e' o intervalo de quando a tela estava
    // escondida. O aviso ficaria dizendo "tem coisa nova" com a coisa nova aberta
    // na frente dele, e e' assim que um contador ensina a ser ignorado.
    function aoVoltar() {
      if (document.hidden || !vivo) return;
      limparBadge();
      if (timer) clearTimeout(timer);
      void buscar(false);
    }
    document.addEventListener('visibilitychange', aoVoltar);

    buscar(true);
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [juntar, descer]);

  async function enviarTexto() {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const criada = await appApi.enviarTexto(t);
      setTexto('');
      juntar([criada]);
      requestAnimationFrame(() => descer(true));
    } catch (err) {
      setErro((err as ErroApp).message);
    } finally {
      setEnviando(false);
    }
  }

  async function enviarAudio(blob: Blob, mime: string) {
    setEnviando(true);
    setErro(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
        fr.onerror = () => reject(new Error('falha ao ler o audio'));
        fr.readAsDataURL(blob);
      });
      const criada = await appApi.enviarAudio(base64, mime);
      juntar([criada]);
      requestAnimationFrame(() => descer(true));
    } catch (err) {
      setErro((err as ErroApp).message);
    } finally {
      setEnviando(false);
    }
  }

  async function enviarFoto(arquivo: File) {
    setEnviando(true);
    setErro(null);
    try {
      const { base64, mime } = await reduzirFoto(arquivo);
      const criada = await appApi.enviarFoto(base64, mime);
      juntar([criada]);
      requestAnimationFrame(() => descer(true));
    } catch (err) {
      setErro((err as ErroApp).message || 'Nao consegui enviar a foto.');
    } finally {
      setEnviando(false);
    }
  }

  // Colar print pelo BOTAO — e' o unico caminho que funciona no iPhone, onde o
  // evento de colar no campo de texto nao entrega arquivo nenhum.
  async function colarPrint() {
    setMenuFoto(false);
    try {
      const arq = await fotoDoClipboard();
      if (!arq) {
        setErro('Nao achei imagem copiada. Tire o print, toque em Copiar e volte aqui.');
        return;
      }
      await enviarFoto(arq);
    } catch (e) {
      // Recusa do aparelho (ou a pessoa dispensou o "Colar" do iPhone). A saida
      // e' a galeria, onde o print ja esta salvo — nao mandar ninguem procurar
      // permissao em configuracao, que pra clipboard nao existe em lista nenhuma.
      // O codigo entre parenteses e' a sonda: separa recusa do navegador (na hora)
      // de janela nao respondida (12s), que pedem consertos opostos.
      const cod = e instanceof ErroClipboard ? ` (${e.codigo})` : '';
      setErro(`Nao consegui ler o que foi copiado${cod}. Use "Escolher da galeria" — o print fica salvo la.`);
    }
  }

  function alternarSelecao(id: number): void {
    setConfirmandoApagar(false);
    setSelecionadas((atuais) => (atuais.includes(id) ? atuais.filter((x) => x !== id) : [...atuais, id]));
  }

  /**
   * `paraTodos` = some da tela da loja tambem (e o conteudo e' destruido); `false` =
   * so sai daqui, a loja continua vendo ([DA-574]).
   *
   * ⚠️ O servidor **reconfere** a regra e recusa em vez de rebaixar calado — entao um
   * lote que envelheceu entre abrir a folha e confirmar volta com erro explicativo, e
   * nao com um desfecho diferente do que o botao prometeu.
   */
  async function apagarSelecionadas(paraTodos: boolean): Promise<void> {
    if (apagando || !selecionadas.length) return;
    setApagando(true);
    setErro(null);
    try {
      const r = await appApi.apagarMensagens(selecionadas, paraTodos);
      // Some da tela sem deixar marca — a proxima consulta devolve a lapide e o
      // merge por id ja nao tem o que desenhar.
      setMensagens((atuais) => atuais.filter((m) => !r.apagadas.includes(m.id)));
      setSelecionadas([]);
      setConfirmandoApagar(false);
      // ⚠️ Recusa PARCIAL nao some calada: sem esta linha, "apaguei 5 e 2 ficaram"
      // leria como defeito aleatorio da tela.
      if (r.recusadas > 0) {
        setErro(`${r.recusadas} ${r.recusadas === 1 ? 'mensagem não pôde' : 'mensagens não puderam'} ser apagada${r.recusadas === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      setErro((e as ErroApp).message || 'Não consegui apagar.');
    } finally {
      setApagando(false);
    }
  }

  async function reagir(mensagem: MensagemApp, emoji: string) {
    setMenuMsg(null);
    // Reagir de novo com o MESMO emoji tira a reacao — e' como o cliente desfaz
    // sem precisar de um botao "remover" na fileira.
    const minha = (mensagem.reactions ?? []).find((r) => !r.fromMe);
    const alvo = minha?.emoji === emoji ? null : emoji;
    try {
      const r = await appApi.reagir(mensagem.id, alvo);
      aplicarReacoes(mensagem.id, r.reactions);
    } catch (err) {
      setErro((err as ErroApp).message || 'Nao consegui registrar a reacao.');
    }
  }

  let diaAnterior = '';

  // ⚠️ A mensagem da folha sai da LISTA a cada render, nao de uma copia guardada no
  // estado: a reacao que ela mostra chega pela consulta seguinte, e uma copia
  // congelada deixaria o emoji marcado desatualizado com a folha aberta.
  const msgDoMenu = menuMsg === null ? null : (mensagens.find((m) => m.id === menuMsg) ?? null);
  const anexoDoMenu = msgDoMenu ? anexoDe(msgDoMenu) : null;
  // Ha selecao em curso = ha modo de selecao. Ver o comentario do `selecionadas`.
  const emSelecao = selecionadas.length > 0;
  /**
   * O que este lote aceita — decidido pela MESMA peca que o servidor usa pra recusar
   * ([DA-574]). A tela escolhe o que oferecer; o backend cobra de verdade. Duas copias
   * da regra divergiriam no primeiro ajuste, e a que some e' sempre a do backend.
   *
   * ⚠️ Calculado a cada render de proposito, e nao guardado no estado: a janela do
   * "para todos" corre com o relogio, entao uma selecao feita com 59 minutos vira
   * "so pra mim" sozinha ao passar de 60 — e a folha tem que refletir isso, nao um
   * retrato de quando ela abriu.
   */
  const vereditoApagar = decidirApagarLote(
    selecionadas
      .map((id) => mensagens.find((m) => m.id === id))
      .filter((m): m is MensagemApp => !!m)
      .map((m) => ({ minha: m.direcao === 'inbound', criadoEm: m.criado_em })),
  );

  return (
    <div className="app-cliente-root" style={{ height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: '#f7f7f7', color: '#111' }}>
      <header style={{ backgroundColor: COBRE, color: '#fff', padding: '12px 12px 12px 4px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* ⚠️ Este canto era o ☰ ate 18/08/2026. Com a HOME virando a porta de
            entrada ([DA-575]), o chat deixou de ser a raiz e passou a ser uma area
            como as outras — entao aqui mora a SETA, e o menu voltou pra home, junto
            do Sair. Ter os tres (voltar, menu e sair) no mesmo cabecalho poria o
            botao de encerrar a sessao a um toque do de voltar, e essa vizinhanca ja
            e' cara de mais numa tela de celular. */}
        <button
          type="button"
          onClick={aoVoltar}
          aria-label="Voltar"
          style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}
        >
          <ChevronLeft size={22} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: 0.3 }}>FIO DE COBRE</div>
          <div style={{ fontSize: 12.5, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cliente.nome}
          </div>
        </div>
      </header>

      <div ref={rolagemRef} onScroll={aoRolar} style={{ flex: 1, overflowY: 'auto', padding: '14px 12px 8px' }}>
        {carregando ? (
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 24 }}>Carregando...</p>
        ) : mensagens.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 32, padding: '0 24px', lineHeight: 1.6 }}>
            <p style={{ fontWeight: 600, color: '#374151' }}>Escreva a sua primeira mensagem.</p>
            {/* A promessa e' sobre o CANAL ("chega e fica registrado"), nunca sobre o
                tempo de resposta: se as tres estiverem no balcao, a mensagem espera
                igual — e prometer rapidez que nao se cumpre queima mais rapido aqui
                do que no WhatsApp, porque a marca na tela e' a da loja. */}
            <p style={{ marginTop: 8 }}>A loja recebe aqui mesmo e responde por esta tela.</p>
          </div>
        ) : (
          mensagens.map((m) => {
            const dia = diaLegivel(m.criado_em);
            const mostrarDia = dia !== diaAnterior;
            diaAnterior = dia;
            const meu = m.direcao === 'inbound';
            const reacoes = m.reactions ?? [];
            const anexo = anexoDe(m);
            /**
             * UM botao por mensagem, no mesmo lugar em toda linha ([DA-573]).
             *
             * Ele abre a folha com o que AQUELA mensagem aceita. Antes eram dois
             * icones lado a lado por balao (marcar + compartilhar do meu lado,
             * reagir + compartilhar do lado da loja) — *"esta feio estes dois
             * botoes"*, e o balao ainda cedia largura pra caber os dois.
             */
            // ⛔ No espelho o `⋮` nao existe ([DA-634]): tudo que ele abre (reagir,
            //    apagar, selecionar) e' gravacao, e o servidor recusa as tres.
            //    Botao que abre um menu inteiro de acoes proibidas e' pior que
            //    botao nenhum.
            const botaoMenu = cliente.espelho ? null : (
              <button
                type="button"
                onClick={() => setMenuMsg(m.id)}
                aria-label="Mais opções para esta mensagem"
                style={{
                  width: 30, height: 30, flexShrink: 0, borderRadius: 999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#a8a29e', backgroundColor: 'transparent',
                }}
              >
                {/* ⋮ VERTICAL, nao horizontal — e' o desenho que o resto do sistema usa
                    pra "mais opcoes" (13 arquivos, incluindo o `MensagemBubble`, que e'
                    o LADO DA LOJA desta mesma conversa). O horizontal que nasceu aqui
                    era a unica ocorrencia no front inteiro: os dois lados do mesmo chat
                    desenhavam o mesmo botao de dois jeitos. */}
                <MoreVertical size={18} />
              </button>
            );
            /**
             * A caixinha OCUPA o lugar do `⋮` durante a selecao — um modo, um trabalho.
             *
             * ⚠️ **Vale nos DOIS lados desde 18/08/2026** ([DA-574], pedido dele: *"o
             * botao de selecionar varias deve aparecer em todas as fotos ou
             * mensagens"*). Antes so as dele entravam, porque so as dele podiam ser
             * apagadas; agora toda mensagem pode ao menos sair da tela DELE.
             */
            const botaoSelecao = (
              <button
                type="button"
                onClick={() => alternarSelecao(m.id)}
                aria-label={selecionadas.includes(m.id) ? 'Tirar da seleção' : 'Selecionar esta mensagem'}
                style={{
                  width: 30, height: 30, flexShrink: 0, borderRadius: 999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: selecionadas.includes(m.id) ? COBRE : '#a8a29e',
                  backgroundColor: 'transparent',
                }}
              >
                {selecionadas.includes(m.id) ? <CheckSquare size={18} /> : <Square size={17} />}
              </button>
            );
            return (
              <div key={m.id}>
                {mostrarDia && (
                  <div style={{ textAlign: 'center', margin: '14px 0 10px' }}>
                    <span style={{ fontSize: 12, color: '#6b7280', backgroundColor: '#e7e5e4', padding: '3px 10px', borderRadius: 999 }}>{dia}</span>
                  </div>
                )}
                {/* ⚠️ `center`, nao `flex-end`: o botao acompanha o MEIO da altura do
                    balao. No pe ele ficava a 316px do topo de uma foto de 331px — longe
                    da foto que ele opera, e' o polegar atravessando a imagem toda pra
                    alcancar. Fica FORA do balao de proposito: sobre a foto ele cobriria
                    justamente o miolo, que neste app costuma ser print de orcamento. */}
                <div style={{ display: 'flex', justifyContent: meu ? 'flex-end' : 'flex-start', alignItems: 'center', gap: 4, marginBottom: reacoes.length ? 16 : 8 }}>
                  {/* Do lado do cliente o botao vai ANTES do balao: espelho do lado da
                      loja, e o balao continua encostado na propria borda.

                      ⚠️ **Em selecao a caixinha OCUPA o lugar do "...", nao se soma a
                      ele** ([DA-573]) — um modo, um trabalho. E' isso que tira a
                      caixinha da tela nos outros 99% do tempo, que era metade da
                      feiura: ela ficava desenhada em toda mensagem minha, sempre,
                      inclusive quando ninguem ia apagar nada. */}
                  {meu && (emSelecao ? botaoSelecao : botaoMenu)}
                  <div
                    style={{
                      position: 'relative',
                      maxWidth: '82%',
                      padding: m.tipo === 'image' ? 4 : '9px 12px',
                      paddingBottom: reacoes.length ? 16 : undefined,
                      borderRadius: 14,
                      backgroundColor: meu ? '#fde8c8' : '#fff',
                      border: meu ? 'none' : '1px solid #e7e5e4',
                      fontSize: 15,
                      lineHeight: 1.45,
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.tipo === 'audio' && m.midia_url ? (
                      <PlayerAudio src={m.midia_url} cor={COBRE} />
                    ) : m.tipo === 'image' && anexo ? (
                      // ⚠️ **A legenda vem ABAIXO da foto, pelo mesmo motivo do PDF logo
                      // adiante** — ela comenta a imagem, nao a substitui. Ate 26/08/2026
                      // este ramo desenhava SO a `<img>`: toda foto que a loja mandasse
                      // com texto junto chegava aqui **muda**, e ninguem podia perceber,
                      // porque no lado da loja o texto aparecia normal ([DA-621]).
                      // Foi o que segurou o Pix no app: o QR nao serve pra quem ja esta
                      // no celular, e a CHAVE — a unica parte util — mora na legenda.
                      <>
                      <img
                        src={anexo}
                        alt="Foto"
                        // ⚠️ **`width`/`height` aqui NAO sao o tamanho na tela — sao a
                        // PROPORCAO que reserva o espaco antes da foto chegar.** Sem
                        // eles o balao de uma foto que ainda baixa tem 8px de altura,
                        // e uma conversa de fotos vira uma coluna de carimbos de hora
                        // num fundo vazio — foi o que o Ruan viu em 17/08/2026 e
                        // chamou, com razao, de "tela branca". O navegador usa esta
                        // proporcao so enquanto nao conhece a real; assim que a foto
                        // carrega, o `height: auto` volta a mandar e a altura vira a
                        // verdadeira. 3:4 e' o retrato de celular; o orcamento e' mais
                        // comprido (1:1.9, medido), entao ainda sobra um ajuste — e' o
                        // que o `onLoad` acerta. ⛔ Reservar EXATO exige guardar a
                        // dimensao no upload; esta em `pendencias.md`.
                        width={1200}
                        height={1600}
                        // So baixa o que esta perto dos olhos. A conversa da CRISTINA
                        // sao 19 fotos, 4,7 MB — baixar tudo na abertura era metade do
                        // "demorando muito".
                        loading="lazy"
                        onLoad={aoCarregarFoto}
                        onClick={() => setFotoAberta({ url: anexo, criadoEm: m.criado_em })}
                        // O fundo aparece SO enquanto a foto nao chegou (imagem
                        // carregada cobre ele). E' o que faz o lugar reservado ler como
                        // "foto chegando" em vez de buraco branco.
                        style={{ display: 'block', maxWidth: '100%', width: 230, height: 'auto', borderRadius: 11, cursor: 'zoom-in', backgroundColor: '#ece9e6' }}
                      />
                      {m.texto ? (
                        // `userSelect` explicito: o balao inteiro e' clicavel pra abrir a
                        // foto, e sem isto segurar o dedo sobre a chave do Pix abriria o
                        // visualizador em vez de deixar copiar — que e' o gesto que o
                        // cliente precisa fazer ali.
                        <span style={{ display: 'block', whiteSpace: 'pre-wrap', marginTop: 6, padding: '0 4px 2px', userSelect: 'text' }}>{m.texto}</span>
                      ) : null}
                      </>
                    ) : m.tipo === 'document' && m.midia_url ? (
                      // O PDF que a loja mandou ([DA-572]). A legenda, quando existe,
                      // vem ABAIXO do cartao: ela comenta o arquivo, nao o substitui —
                      // e sem isto o texto que a vendedora escreveu junto sumiria.
                      <>
                        <AnexoDocumento url={m.midia_url} nome={m.midia_nome} bytes={m.midia_bytes} meu={meu} />
                        {m.texto ? (
                          <span style={{ display: 'block', whiteSpace: 'pre-wrap', marginTop: 6 }}>{m.texto}</span>
                        ) : null}
                      </>
                    ) : (
                      <span style={{ whiteSpace: 'pre-wrap' }}>{m.texto}</span>
                    )}
                    {/* O check so existe na mensagem do CLIENTE — e' ele quem espera
                        resposta. Dois estados, nao tres: aqui a loja e' o proprio
                        sistema, entao "chegou no servidor" e "chegou no destino" sao o
                        mesmo instante, e um traco solitario nunca seria verdade sobre
                        coisa nenhuma. Azul = alguem abriu a conversa (DA-538). */}
                    <div style={{ fontSize: 11, color: '#78716c', textAlign: 'right', marginTop: 3, paddingRight: m.tipo === 'image' ? 4 : 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                      {horaCurta(m.criado_em)}
                      {meu && (
                        <CheckCheck
                          size={15}
                          color={m.lida_loja ? '#2563eb' : '#a8a29e'}
                          aria-label={m.lida_loja ? 'Lida pela loja' : 'Entregue a loja'}
                        />
                      )}
                    </div>
                    {reacoes.length > 0 && (
                      <div
                        style={{
                          position: 'absolute', bottom: -12, left: 10,
                          display: 'flex', gap: 2, padding: '2px 7px',
                          backgroundColor: '#fff', border: '1px solid #e7e5e4',
                          borderRadius: 999, fontSize: 13, lineHeight: 1.3,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        }}
                      >
                        {reacoes.map((r, i) => (
                          <span key={`${r.jid}-${i}`} title={r.fromMe ? 'Fio de Cobre' : r.name}>{r.emoji}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Botao SEMPRE visivel: recurso que so aparece depois de um gesto
                      escondido (segurar o dedo) nao existe pra quem nunca soube dele. */}
                  {!meu && (emSelecao ? botaoSelecao : botaoMenu)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {erro && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '8px 14px', fontSize: 13.5 }}>{erro}</div>
      )}

      {/* A barra do lote: aparece SO com alguma coisa marcada, logo acima do campo de
          escrever, que e' onde o polegar ja esta. */}
      {selecionadas.length > 0 && (
        <div style={{ backgroundColor: '#fff8ef', borderTop: '1px solid #f0d5b0', padding: '10px 14px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* ⚠️ **Sem a palavra "mensagem"**: com ela o texto nao cabe ao lado dos
                dois botoes na largura de um iPhone e quebra em DUAS linhas,
                engordando a barra (medido a 390px, 40,6px onde cabia 20). A palavra
                tambem nao informa nada aqui — as caixinhas estao na tela. */}
            <span style={{ flex: 1, fontSize: 14, color: '#5b4636' }}>
              {selecionadas.length === 1 ? '1 selecionada' : `${selecionadas.length} selecionadas`}
            </span>
            <button
              type="button"
              onClick={() => { setSelecionadas([]); setConfirmandoApagar(false); }}
              style={{ height: 40, padding: '0 14px', borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 14, fontWeight: 700 }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => setConfirmandoApagar(true)}
              style={{ height: 40, padding: '0 14px', borderRadius: 9, border: 'none', backgroundColor: '#b91c1c', color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Trash2 size={16} /> Apagar
            </button>
          </div>
        </div>
      )}

      {/* ⛔ **No ESPELHO a barra de digitar da lugar a uma tarja** ([DA-634]). Ela
          nao pode so ficar cinza: o servidor recusa TODA gravacao, e um campo que
          aceita texto e some com ele no envio e' o defeito que o principio 1 do
          `sistema/CLAUDE.md` chama de "campo que aceita a digitacao e joga fora em
          silencio". A tarja ocupa o mesmo lugar, entao a tela que eu confiro
          continua tendo a mesma altura util do chat de verdade. */}
      {cliente.espelho ? (
        <div style={{
          padding: '14px 12px calc(14px + env(safe-area-inset-bottom))', backgroundColor: '#fff',
          borderTop: '1px solid #e7e5e4', flexShrink: 0,
          fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.45,
        }}>
          Modo espelho — você está vendo a conversa, sem poder responder.
        </div>
      ) : (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 8, padding: 10, backgroundColor: '#fff', borderTop: '1px solid #e7e5e4', flexShrink: 0 }}>
        <textarea
          className="no-upper"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            // Enter envia no teclado fisico; no celular o Enter e' quebra de linha
            // (o teclado virtual nao manda shiftKey), entao o botao e' o caminho.
            if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
              e.preventDefault();
              void enviarTexto();
            }
          }}
          // Colar print (Ctrl+V no PC, "colar" do teclado no celular). Vai direto,
          // igual camera e galeria — este chat nao tem area de anexo pendente, e
          // inventar uma so pro colar criaria dois jeitos de mandar foto.
          onPaste={(e) => {
            if (enviando) return;
            const arq = fotoDoColar(e);
            if (arq) void enviarFoto(arq);
          }}
          rows={1}
          placeholder="Escreva para a loja..."
          // Corretor do celular: o campo do /atendimento ja declara os tres, este
          // nascera sem. Sem eles o comportamento fica na mao do default do
          // navegador, que varia por aparelho e por webview (o link do convite
          // abre dentro do WhatsApp, que nao e o Safari puro).
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck
          style={{
            flex: 1,
            resize: 'none',
            maxHeight: 120,
            minHeight: 44,
            padding: '11px 14px',
            fontSize: 16,
            border: '1px solid #d1d5db',
            borderRadius: 22,
            backgroundColor: '#fff',
            color: '#111',
          }}
        />
        {texto.trim() ? (
          <button
            type="button"
            onClick={() => void enviarTexto()}
            disabled={enviando}
            aria-label="Enviar"
            style={{ width: 44, height: 44, borderRadius: 999, backgroundColor: COBRE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <Send size={19} />
          </button>
        ) : (
          <>
            {/* DOIS caminhos de proposito, porque um `input` so nao serve os dois
                sistemas: sem `capture`, o iPhone pergunta "Tirar foto ou Fototeca?"
                e o **Android vai direto pra galeria**, sem oferecer a camera
                (relatado no 1o uso real em Android, 14/08); com `capture`, os dois
                abrem a camera e a galeria some — e metade das fotos que a loja
                precisa ver ja esta salva no celular (print do orcamento, foto da
                peca antiga). Entao a escolha vira nossa, igual nos dois aparelhos,
                em vez de depender do que cada navegador resolve fazer. */}
            <input
              ref={fotoInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const arq = e.target.files?.[0];
                // Zerar o valor deixa a MESMA foto ser escolhida de novo (sem isso o
                // segundo envio do mesmo arquivo nao dispara o onChange).
                e.target.value = '';
                if (arq) void enviarFoto(arq);
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                const arq = e.target.files?.[0];
                e.target.value = '';
                if (arq) void enviarFoto(arq);
              }}
            />
            <button
              type="button"
              onClick={() => setMenuFoto(true)}
              disabled={enviando}
              aria-label="Enviar foto"
              style={{ width: 44, height: 44, borderRadius: 999, color: COBRE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              <Camera size={21} />
            </button>
            <GravadorVoz onGravado={(b, m) => void enviarAudio(b, m)} desabilitado={enviando} />
          </>
        )}
      </div>
      )}

      {menuFoto && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-end',
          }}
        >
          {/* Sem fechar por toque no fundo (regra da casa): sai pelo Cancelar. */}
          <div
            style={{
              width: '100%', backgroundColor: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              padding: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <button
              type="button"
              onClick={() => { setMenuFoto(false); cameraInputRef.current?.click(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
            >
              <Camera size={22} color={COBRE} /> Tirar foto
            </button>
            <button
              type="button"
              onClick={() => { setMenuFoto(false); fotoInputRef.current?.click(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
            >
              <Galeria size={22} color={COBRE} /> Escolher da galeria
            </button>
            <button
              type="button"
              onClick={() => void colarPrint()}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
            >
              <ClipboardPaste size={22} color={COBRE} /> Colar print
            </button>
            <button
              type="button"
              onClick={() => setMenuFoto(false)}
              style={{ padding: '14px 14px', fontSize: 16, color: '#6b7280', borderTop: '1px solid #e5e7eb', marginTop: 4 }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* A folha de CONFIRMAR o apagar ([DA-574]).
          Ela existe porque agora ha DOIS apagares, e eles fazem coisas opostas: um
          destroi a mensagem dos dois lados, o outro so tira ela desta tela. Uma frase
          so nao consegue descrever os dois, e a frase antiga ("some tambem da tela da
          loja") viraria mentira metade das vezes.

          ⚠️ **Cada botao diz o que faz na PROPRIA linha.** Sem isso os dois viram
          "Apagar" e "Apagar", indistinguiveis no instante em que a pessoa aperta —
          que e' exatamente quando importa.

          ⚠️ **Quando so ha uma saida, o "para todos" NAO aparece cinza — ele nao
          existe**, e a folha diz o motivo em texto. Botao apagado e mudo e' beco sem
          saida (regra da casa, § UI). */}
      {confirmandoApagar && selecionadas.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end' }}>
          <div
            style={{
              width: '100%', backgroundColor: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              padding: '10px 10px calc(10px + env(safe-area-inset-bottom))',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111', padding: '8px 14px 2px' }}>
              Apagar {selecionadas.length === 1 ? 'esta mensagem' : `estas ${selecionadas.length} mensagens`}?
            </div>
            {vereditoApagar.motivo && (
              <div style={{ fontSize: 13, color: '#6b7280', padding: '0 14px 6px', lineHeight: 1.45 }}>
                Só dá pra tirar da sua tela: a seleção tem mensagem{' '}
                <strong>{vereditoApagar.motivo === 'recebida' ? 'recebida' : `enviada há mais de ${JANELA_APAGAR_PARA_TODOS_MIN} minutos`}</strong>
                , e essas não saem do lado da loja.
              </div>
            )}
            {vereditoApagar.paraTodos && (
              <button
                type="button"
                onClick={() => void apagarSelecionadas(true)}
                disabled={apagando}
                style={{
                  display: 'block', width: '100%', padding: '13px 14px', borderRadius: 12,
                  border: 'none', backgroundColor: '#b91c1c', color: '#fff', textAlign: 'left',
                  opacity: apagando ? 0.6 : 1,
                }}
              >
                <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>Apagar para todos</span>
                <span style={{ display: 'block', fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>
                  Some também da tela da loja. Não dá pra desfazer.
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => void apagarSelecionadas(false)}
              disabled={apagando}
              style={{
                display: 'block', width: '100%', padding: '13px 14px', borderRadius: 12,
                border: '1px solid #d6d3d1', backgroundColor: 'transparent', color: '#44403c',
                textAlign: 'left', opacity: apagando ? 0.6 : 1,
              }}
            >
              <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>Apagar só pra mim</span>
              <span style={{ display: 'block', fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>
                Sai daqui. A loja continua vendo.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setConfirmandoApagar(false)}
              style={{ padding: 13, fontSize: 16, color: '#6b7280', borderTop: '1px solid #e5e7eb', marginTop: 2, width: '100%' }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* A folha de opcoes de UMA mensagem (18/08/2026 — [DA-573]).
          Gatilho dele: *"Esta feio estes dois botoes. Tem como colocar um unico botao,
          como o menu popup (...)?"*

          **Folha de baixo, nao balao ancorado.** A lista rola dentro de um
          `overflow-y: auto`, e dropdown ali dentro exige `position: fixed` +
          `getBoundingClientRect` com virada pra cima quando nao cabe (regra da casa).
          A folha nao precisa de nada disso, ja e' o padrao da casa neste mesmo arquivo
          (a folha da camera, logo acima) e num celular ela nasce onde o polegar esta.

          **O conteudo varia por mensagem, e e' por isso que UM botao serve os quatro
          casos**: minha com anexo (compartilhar + selecionar), minha so texto
          (selecionar), da loja com anexo (reagir + compartilhar), da loja so texto
          (reagir). O que nao cabe simplesmente nao aparece — nao ha item apagado
          dizendo "prefira outro caminho".

          ⚠️ **Reagir NAO ficou mais longe**: hoje tambem eram dois toques (abrir a
          fileira, escolher o emoji). A fileira mudou de lugar, nao de custo. Quem
          perdeu um toque foi o compartilhar — e o botao direto sobrevive na foto
          aberta em tela cheia, que e' de onde ele mais sai ([DA-524] p.9). */}
      {msgDoMenu && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-end',
          }}
        >
          {/* Sem fechar por toque no fundo (regra da casa): sai pelo Cancelar. */}
          <div
            style={{
              width: '100%', backgroundColor: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              padding: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <div style={{ fontSize: 12.5, color: '#6b7280', padding: '6px 14px 10px', borderBottom: '1px solid #f0efee', marginBottom: 4 }}>
              {tituloDaMensagem(msgDoMenu)}
            </div>
            {/* Reagir e' a propria fileira, sem gatilho intermediario — item de menu
                que abre outra fileira seria o terceiro toque. So na mensagem da LOJA:
                reagir a si mesmo nao existe no WhatsApp e nao existe aqui. */}
            {msgDoMenu.direcao === 'outbound' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 6px 8px' }}>
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => void reagir(msgDoMenu, e)}
                    aria-label={`Reagir com ${e}`}
                    style={{
                      width: 46, height: 46, borderRadius: 999, fontSize: 25,
                      lineHeight: 1, backgroundColor: reacaoDoCliente(msgDoMenu) === e ? '#fde8c8' : 'transparent',
                    }}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
            {anexoDoMenu && (
              <BotaoCompartilhar
                url={anexoDoMenu}
                mime={msgDoMenu.midia_mime}
                criadoEm={msgDoMenu.criado_em}
                // So o documento tem nome que valha a pena preservar — ver o comentario
                // do proprio `BotaoCompartilhar` ([DA-572]).
                nome={msgDoMenu.tipo === 'document' ? msgDoMenu.midia_nome : null}
                rotulo={rotuloCompartilhar(msgDoMenu)}
                variante="linha-de-menu"
                aoAcionar={() => setMenuMsg(null)}
              />
            )}
            {/* Entrar no modo JA marcando esta mensagem: quem abriu a folha desta
                mensagem quer esta mensagem. Sem isso o modo nasceria vazio — e vazio
                nao existe, ele E' a lista ([DA-573]).
                ⚠️ Em TODA mensagem desde [DA-574] — o que muda entre elas nao e' poder
                selecionar, e' o que a folha de confirmacao vai oferecer depois. */}
            {(
              <button
                type="button"
                onClick={() => {
                  setSelecionadas([msgDoMenu.id]);
                  setConfirmandoApagar(false);
                  setMenuMsg(null);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
              >
                <CheckSquare size={22} color={COBRE} /> Selecionar várias
              </button>
            )}
            <button
              type="button"
              onClick={() => setMenuMsg(null)}
              style={{ padding: '14px 14px', fontSize: 16, color: '#6b7280', borderTop: '1px solid #e5e7eb', marginTop: 4 }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {fotoAberta && (
        <div
          onClick={() => setFotoAberta(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50, backgroundColor: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
          }}
        >
          <img src={fotoAberta.url} alt="Foto" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          {/* Compartilhar tambem daqui: e' olhando a foto grande que a vontade de
              repassar aparece. O mime vem do proprio download. */}
          <div style={{ position: 'absolute', top: 14, right: 66 }}>
            <BotaoCompartilhar
              url={fotoAberta.url}
              criadoEm={fotoAberta.criadoEm}
              rotulo="Compartilhar esta foto"
              variante="sobreposto"
            />
          </div>
          {/* O X existe alem do toque no fundo: fechar so pelo fundo e' gesto que
              ninguem garante que a pessoa conhece. */}
          <button
            type="button"
            onClick={() => setFotoAberta(null)}
            aria-label="Fechar"
            style={{
              position: 'absolute', top: 14, right: 14, width: 44, height: 44, borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.16)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={22} />
          </button>
        </div>
      )}
    </div>
  );
}
